import { defaultSchema, type Options } from 'rehype-sanitize';

import { TRUSTED_EMBED_ATTRIBUTES } from './rehypeTrustedEmbeds';

/**
 * The allowlist every backend rich-text field passes before it renders.
 *
 * It starts from `defaultSchema`, the GitHub rule set `rehype-sanitize` ships.
 * That set keeps the elements a shop's editor produces — headings, paragraphs,
 * lists, tables, links, images, emphasis, quotes and code — and drops every
 * `on*` handler, every `style` and `class` attribute, and every `href` or
 * `src` whose protocol is not on its list. A `javascript:` URL therefore loses
 * its attribute, and `id` and `name` get a `user-content-` prefix so copy
 * cannot clobber a page global. `rehypeInPageAnchors` points in-page links at
 * the prefixed names.
 *
 * The additions are what catalogue copy uses and the GitHub set lacks:
 *
 * - `u` and `small`, which product copy uses for emphasis and fine print;
 * - `figure` and `figcaption` around an image or a table;
 * - a table's `caption`, `colgroup` and `col`, held inside a `table` like the
 *   rows are;
 * - a link's `target="_blank"` and its `rel` tokens, so a link that opens a
 *   new tab still does. A browser gives a `_blank` link no opener unless
 *   `rel` asks for one, and `opener` is not on the list;
 * - `iframe`, with the attributes a video player needs. The schema cannot
 *   judge an iframe's `src`, so `rehypeTrustedEmbeds` runs after it and
 *   removes every iframe that is not a video embed from a trusted host.
 *
 * One removal: `input`. The GitHub set keeps an input as a disabled checkbox
 * for GFM task lists, and turns every other input into one too. Copy holds no
 * form, so an input goes entirely, and a task list keeps its items without
 * checkboxes.
 *
 * An element off the list is unwrapped: its children stay and the tag goes.
 * An element on `strip` goes with its content, because that content is code,
 * CSS or an embed rather than copy, and unwrapping it would print it as text.
 */
export const markdownSanitizeSchema: Options = {
  ...defaultSchema,
  tagNames: [
    ...(defaultSchema.tagNames ?? []).filter((name) => name !== 'input'),
    'u',
    'small',
    'figure',
    'figcaption',
    'caption',
    'colgroup',
    'col',
    'iframe',
  ],
  attributes: {
    ...Object.fromEntries(
      Object.entries(defaultSchema.attributes ?? {}).filter(([tagName]) => tagName !== 'input')
    ),
    a: [
      ...(defaultSchema.attributes?.a ?? []),
      ['target', '_blank'],
      ['rel', 'nofollow', 'noopener', 'noreferrer', 'sponsored', 'ugc'],
    ],
    iframe: [...TRUSTED_EMBED_ATTRIBUTES],
  },
  required: {},
  ancestors: {
    ...defaultSchema.ancestors,
    caption: ['table'],
    colgroup: ['table'],
    col: ['table'],
  },
  strip: [
    ...(defaultSchema.strip ?? []),
    'style',
    'object',
    'embed',
    'input',
    'noscript',
    'template',
    'textarea',
    'title',
  ],
};
